// SUB-OFFICE 7 — THE MACHINE FLOOR CONTROLLER (increment M-D). The soul-layer
// twin of the maintenance floor: it owns the DESCENT CHOREOGRAPHY (the 20-line
// DirectVoice deck, sequenced by DescentScript), the maintenance-station launch
// (client runners), the DIEGETIC VERDICT (the aisle responds — no Stamp Arm on
// -1), and the duty-gated ascent. It is DRIVEN BY VerticalCoreController (which
// owns the cab transition + the light grade + startFullFade); when the player
// is on machine_floor, the vertical controller delegates arrival/tick/interact/
// depart here. This keeps the cab-owns-descent reality clean while giving the
// Machine Floor its own module (spec: machinecontroller.ts).
//
// The register-break is the payoff: the whole building above speaks in the THIRD
// person (ALL CAPS, housed, procedural). Down here it drops the memo format and
// says "you" — sentence case, unhoused, once at a time, warm amber, the ONLY
// lowercase text in the building. DirectVoice guards that jealously.
//
// NOTE ON ENTRY: the CONSCRIPTION STAIR entry (movement I/II — floor7 marches
// you to the Dept-6 stairhead, unmasked bone->phosphor walk) depends on M-C
// (ConscriptionController + floor7 spawn 'descent_return'), NOT yet shipped. So
// M-D wires the CAB arrival (entry='cab'): the deck starts at the arrival
// landing (THESIS 1), the stair-only opener lines (idx 0/9/4) are held for the
// stair route. The startFullFade primitive still ships (in VerticalCore) so the
// stair drops in with no rework.

import { DirectVoice } from '../../ui/directvoice';
import { launchMaintenance, type Band } from '../../physical/maintenance';
import type { MachineFloorBuild } from './machine';
import content from '../../../content/machine-floor.json';

const machineDemands = (): string[] => content.machineFloor.maintenanceDemands;

// island offset (must match machine.ts OX/OZ) — used to map world pos -> local.
const OX = 320, OZ = 0;

export interface MachineFloorDeps {
  dom: { say: (t: string, h?: number) => void; setPrompt: (t: string | null) => void };
  bus: { emit: (e: string, p?: unknown) => void };
  reducedMotion: boolean;
}

/** one DirectVoice beat: an index into machineFloor.directAddress + the spatial
 *  condition (local x,z) that surfaces it. Fired in order, at most one per beat
 *  gap, never repeated within a shift. */
interface Beat { idx: number; test: (lx: number, lz: number) => boolean; hold?: number }

export class MachineFloorController {
  private voice: DirectVoice;
  private build: MachineFloorBuild | null = null;
  private active = false;
  private busy = false;             // a maintenance runner is open
  private completedDuties = 0;
  private surfaced = new Set<number>();
  private beatGap = 0;              // spacing so lines never crowd
  private arriveTimers: { idx: number; t: number }[] = [];
  private lines: string[];

  constructor(private deps: MachineFloorDeps, directAddress: string[]) {
    this.voice = new DirectVoice(deps.reducedMotion);
    this.lines = directAddress;
  }

  get isActive(): boolean { return this.active; }
  /** true while a maintenance runner overlay is open (holds the deck + hotspots). */
  get isBusy(): boolean { return this.busy; }
  /** the ascent stair reads LOCKED until the round is discharged (spec hotspot). */
  get dutyDischarged(): boolean { return this.completedDuties >= 1; }

  /** The positional deck (cab entry). Stair-only openers (0/9/4) are omitted here
   *  and belong to the stair route. Ordered south->north as the player advances. */
  private readonly beats: Beat[] = [
    { idx: 3, test: (_x, z) => z < 9 },                          // Rack Hall A 1/3
    { idx: 1, test: (_x, z) => z < 2 },                          // Rack Hall A 2/3
    { idx: 6, test: (_x, z) => z < -4 },                         // Hall A end (the floor humming)
    { idx: 8, test: (_x, z) => z < -6 },                         // leaving Hall A (nothing to compose)
    { idx: 2, test: (x, z) => z < -9 && Math.abs(x) < 5 },       // Hall B crossing
    { idx: 17, test: (x) => x < -20 },                           // coolant gallery (not made of paper)
    { idx: 10, test: (x) => x < -28 },                           // the green was never for you
    { idx: 13, test: (x) => x < -34 },                           // endlessly, thorough (the stillest beat)
    { idx: 16, test: (_x, z) => z < -14 },                       // entering the nave (it is a floor)
    { idx: 14, test: (_x, z) => z < -20 },                       // THE APERTURE — THESIS 3 (summit)
  ];

  /** Called by VerticalCoreController once the cab doors open on Floor -1. */
  onArrive(build: MachineFloorBuild, entry: 'cab' | 'stair'): void {
    this.build = build;
    this.active = true;
    this.busy = false;
    this.surfaced.clear();
    this.completedDuties = 0;
    this.beatGap = 0;
    // arrival cluster: THESIS 1 then the footrace (the canister already gone north)
    this.arriveTimers = entry === 'cab'
      ? [{ idx: 5, t: 1.6 }, { idx: 15, t: 7.5 }]
      : [{ idx: 4, t: 0.6 }, { idx: 5, t: 3.0 }, { idx: 15, t: 8.5 }];
    this.deps.bus.emit('audio:machinefloor', { on: true });
  }

  /** Tick the DirectVoice caption fade. Called EVERY active frame by the vertical
   *  controller (even off the floor / during ride-up) so the withdrawal + the
   *  forgetting lines fade cleanly after departure. */
  tickVoice(dt: number): void { this.voice.update(dt); }

  update(dt: number, worldX: number, worldZ: number): void {
    if (!this.active || !this.build) return;
    if (this.busy) return; // a runner overlay is up; hold the deck
    // scheduled arrival lines
    for (const a of this.arriveTimers) {
      if (a.t > 0) { a.t -= dt; if (a.t <= 0) this.speak(a.idx); }
    }
    // positional beats — at most one per ~5.5s, never crowding the duties
    this.beatGap -= dt;
    if (this.beatGap <= 0) {
      const lx = worldX - OX, lz = worldZ - OZ;
      for (const b of this.beats) {
        if (!this.surfaced.has(b.idx) && b.test(lx, lz)) { this.speak(b.idx); break; }
      }
    }
  }

  private speak(idx: number): void {
    if (this.surfaced.has(idx)) return;
    this.surfaced.add(idx);
    this.beatGap = 5.5;
    this.deps.bus.emit('audio:voiceduck', { on: true });
    this.voice.speak(this.lines[idx], 8);
  }

  /** Resolve a hotspot interaction. Returns true if handled here (so the vertical
   *  controller stops). Maintenance stations launch a client runner. */
  interact(id: string): boolean {
    if (!this.build) return false;
    if (this.busy) return true; // a runner overlay is up — swallow interaction
    const mf = machineDemands();
    switch (id) {
      case 'coolant_valve': this.runStation('6-K'); return true;
      case 'rack_rite_a': this.runStation('6-P'); return true;
      // honest stubs: the station is dressed + the duty reads, but the runner is
      // deferred to the worker lane (gen/verify) — acknowledge the demand line.
      case 'coolant_gauge_n': this.stub(mf[3]); return true;   // 2-V WITNESS_ONLY
      case 'terminal_a': this.stub(mf[6]); return true;        // 4-T reprise
      case 'intake_hatch': this.stub(mf[2]); return true;      // 6-F feed
      case 'drawer_reseat_a': this.stub(mf[1]); return true;   // 6-R re-seat
      default: return false;                                    // cab_up etc. handled upstream
    }
  }

  private stub(demandLine: string): void {
    this.deps.dom.say(demandLine, 7);
    this.deps.bus.emit('audio:voiceduck', { on: true });
  }

  private runStation(system: '6-K' | '6-P'): void {
    const p = launchMaintenance(system, {
      reducedMotion: this.deps.reducedMotion,
      onCaption: (line) => { /* the runner shows the demand itself */ void line; },
    });
    if (!p) { return; }
    this.busy = true;
    this.voice.clear();
    this.deps.dom.setPrompt(null);
    p.then((band: Band | null) => {
      this.busy = false;
      if (band) this.onDutyComplete(band);
    });
  }

  private onDutyComplete(band: Band): void {
    this.build?.respondVerdict(band);
    this.completedDuties++;
    // the diegetic verdict speaks in the aisle response; the DirectVoice adds the
    // thesis: THESIS 2 on the first duty, then apologies->hands.
    if (this.completedDuties === 1) {
      window.setTimeout(() => this.speak(7), 1400);   // "You maintain the thing that judges you..."
      window.setTimeout(() => { this.surfaced.delete(11); this.speak(11); }, 9000);
    }
    this.deps.bus.emit('audio:machineverdict', { band });
  }

  /** Called by VerticalCoreController when ascending away (cab or stair). Fires
   *  the withdrawal + forgetting lines; the fade/rebuild is owned upstream. */
  onDepart(): void {
    // only fire the withdrawal beats if we were actually on the floor (guards a
    // second call from teardown, and the stair/cab both routing through here).
    if (this.active && this.surfaced.size > 0) {
      this.voice.speak(this.lines[18], 5);            // withdrawal begins
      window.setTimeout(() => { if (!this.active) this.voice.speak(this.lines[19], 6); }, 3200); // go up and forget
    }
    this.active = false;
    this.busy = false;
    this.surfaced.clear();
    this.arriveTimers = [];
    this.deps.bus.emit('audio:machinefloor', { on: false });
  }

  dispose(): void {
    this.voice.dispose();
    this.build = null;
    this.active = false;
    this.busy = false;
    this.surfaced.clear();
  }
}
